import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { find } from 'lodash';

import { Margin, Padding } from 'styled-components-spacing';
import { useFormFields, filterHonoraryRoles } from 'helpers';
import {
  loadMaPeopleAddListUsersCreator,
  loadGlobalMaRolesCreator,
  addMaPeopleUsersCreator,
} from 'store/actionsCreators';
import {
  Alerts,
  Button,
  Checkbox,
  Col,
  Container,
  Dropdown,
  Field,
  Header,
  Heading,
  PageContent,
  Row,
  Select,
  Spinner,
  Table,
} from 'components';
import { useTranslation } from 'react-i18next';

const breadcrumbs = [
  {
    title: 'navigation.breadcrumbs.ma.people',
    url: '/ma/people',
  },
  {
    title: 'navigation.breadcrumbs.ma.addHonorary',
    url: '/ma/people/honorary/add',
  },
];

/**
 * Granting somebody an Honorary Fellowship of this association.
 *
 * Its own screen rather than a mode on Add MA users, because it is a different thing being done
 * to a different set of people: an Honorary Fellow is normally an active member or officer of
 * ANOTHER association, whom the officer search deliberately leaves out. The position they are
 * given here is a real one, minus the seats that would put the two associations in conflict over
 * them - so the list of positions is filtered down to what an Honorary Fellow may hold.
 */
function AddHonorary({ users, roles, loading, loadUsers, loadGlobalMaRoles, addUsers, history }) {
  const { t } = useTranslation();
  const [selectedPeople, setPeople] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);

  const [filterFields, handleFilterFieldChange] = useFormFields({
    firstName: '',
    lastName: '',
    email: '',
  });

  const filters = {
    firstName: { name: 'first-name', label: t('page.ma.peopleAdd.firstName') },
    lastName: { name: 'last-name', label: t('page.ma.peopleAdd.lastName') },
    email: { name: 'email', label: t('page.ma.peopleAdd.email') },
  };

  const availableRoles = filterHonoraryRoles(roles, true);

  useEffect(() => {
    if (!roles.length) {
      loadGlobalMaRoles();
    }
  }, []);

  // A search that no longer returns somebody cannot keep them selected.
  useEffect(() => {
    setPeople(selectedPeople.filter((personId) => find(users, { id: personId })));
  }, [users]);

  useEffect(() => {
    const criteria = Object.keys(filterFields)
      .filter((field) => filterFields[field])
      .reduce((carry, field) => ({ ...carry, [filters[field].name]: filterFields[field] }), {});

    // honorary=1 switches the API to the candidates an Honorary Fellowship can be offered to.
    // With no search term it answers with nobody rather than the whole user table.
    loadUsers({ ...criteria, honorary: 1 });
  }, [filterFields]);

  const hasSearch = Object.keys(filterFields).some((field) => filterFields[field]);

  const handleSelectRow = (id) => (value) => {
    setPeople(
      value ? [...selectedPeople, id] : selectedPeople.filter((personId) => personId !== id),
    );
  };

  const handleChangeRole = (id, select) => ({ value }) => {
    const noDuplicates = selectedRoles.filter(
      (item) => !(item.userId === id && item.select === select),
    );
    setSelectedRoles([...noDuplicates, { userId: id, userRole: value, select }]);
  };

  const submit = () => {
    addUsers(
      selectedPeople.map((item) => {
        const user = { userId: item, userRoles: [], honorary: true };
        selectedRoles.forEach((el) => {
          if (el.userId === item) {
            user.userRoles.push(el.userRole);
          }
        });
        return { ...user };
      }),
      history,
    );
  };

  const renderInput = (field) => (
    <Margin bottom="20" as={Col} size={{ lg: 1 / 3 }} key={`f-${field}`}>
      <Field label={filters[field].label} id={field}>
        <input
          id={field}
          type="text"
          value={filterFields[field]}
          onChange={handleFilterFieldChange}
        />
      </Field>
    </Margin>
  );

  const renderRow = (user) => (
    <Table.Row key={`honorary-add-${user.id}`}>
      <Table.Cell>
        <Field>
          <Checkbox
            onChange={handleSelectRow(user.id)}
            selected={selectedPeople.includes(user.id)}
          />
        </Field>
      </Table.Cell>
      <Table.Cell>
        <Table.Label>{t('page.ma.peopleAdd.firstAndLastName')}:</Table.Label>
        {`${user.firstName} ${user.lastName}`}
      </Table.Cell>
      <Table.Cell>
        <Table.Label>{t('page.ma.peopleAdd.email')}:</Table.Label>
        {user.email}
      </Table.Cell>
      <Table.Cell>
        {/* Worth seeing before granting one: it has to be somebody whose own association is a
            different one, which the API refuses otherwise. */}
        <Table.Label>{t('page.ma.addHonorary.ownMa')}:</Table.Label>
        {(user.memberAssociation && user.memberAssociation.name) || '-'}
      </Table.Cell>
      <Table.Cell>
        <Table.Label>{t('page.ma.peopleAdd.role')}:</Table.Label>
        <Select small onChange={handleChangeRole(user.id, 0)} options={availableRoles} />
      </Table.Cell>
      <Table.Cell>
        <Select small onChange={handleChangeRole(user.id, 1)} options={availableRoles} />
      </Table.Cell>
    </Table.Row>
  );

  return (
    <>
      <Header breadcrumbs={breadcrumbs} title={t('page.ma.addHonorary.title')} />
      <Container>
        <PageContent>
          <Alerts type="ma" />
          <Margin as="p" bottom={{ xs: 16 }}>
            <strong>{t('page.ma.addHonorary.info')}</strong>
          </Margin>
          <Margin as="p" bottom={{ xs: 16 }}>
            {t('page.ma.addHonorary.restrictions')}
          </Margin>
          <form>
            <Row>
              <Margin left={{ xs: 16 }} right={{ xs: 16 }} style={{ width: '100%' }}>
                <Padding horizontal={{ xs: 16 }} vertical={{ xs: 16 }} className="border-xs">
                  <Dropdown
                    openbreakpoint="xl"
                    title={
                      <Heading as="h3" size="h4">
                        {t('page.ma.addHonorary.searchTitle')}
                      </Heading>
                    }
                  >
                    <Row>{Object.keys(filterFields).map((field) => renderInput(field))}</Row>
                  </Dropdown>
                </Padding>
              </Margin>
            </Row>
          </form>
          <Margin top={{ xs: 32 }}>
            {loading ? <Spinner /> : null}
            {!loading ? (
              <Table tablebreakpoint="lg">
                <Table.Head>
                  <Table.Row>
                    <Table.Heading />
                    <Table.Heading>{t('page.ma.peopleAdd.firstAndLastName')}</Table.Heading>
                    <Table.Heading>{t('page.ma.peopleAdd.email')}</Table.Heading>
                    <Table.Heading>{t('page.ma.addHonorary.ownMa')}</Table.Heading>
                    <Table.Heading>{t('page.ma.peopleAdd.role')}</Table.Heading>
                    <Table.Heading />
                  </Table.Row>
                </Table.Head>
                <Table.Body>
                  {!hasSearch ? (
                    <Table.Row>
                      <Table.Cell colSpan={6}>{t('page.ma.addHonorary.searchPrompt')}</Table.Cell>
                    </Table.Row>
                  ) : null}
                  {hasSearch && !users.length ? (
                    <Table.Row>
                      <Table.Cell colSpan={6}>{t('page.ma.addHonorary.noResults')}</Table.Cell>
                    </Table.Row>
                  ) : null}
                  {hasSearch && users.length ? users.map((user) => renderRow(user)) : null}
                </Table.Body>
              </Table>
            ) : null}
          </Margin>
          <Margin top={{ xs: 16 }}>
            <Row>
              <Col size={{ md: 2 / 5, lg: 1 / 4 }}>
                <Button onClick={submit} disabled={!selectedPeople.length}>
                  {t('page.ma.addHonorary.addButton')}
                </Button>
              </Col>
            </Row>
          </Margin>
        </PageContent>
      </Container>
    </>
  );
}

AddHonorary.propTypes = {
  users: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  roles: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  loading: PropTypes.bool.isRequired,
  loadUsers: PropTypes.func.isRequired,
  loadGlobalMaRoles: PropTypes.func.isRequired,
  addUsers: PropTypes.func.isRequired,
  history: PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired,
};

const mapStateToProps = ({ ma, globals, loading }) => ({
  users: ma.people.add.data,
  roles: globals.ma.roles,
  loading: !!loading.filter((item) => item.type === 'MA').length,
});

const mapDispatchToProps = (dispatch) => ({
  loadUsers: (payload) => dispatch(loadMaPeopleAddListUsersCreator(payload)),
  loadGlobalMaRoles: () => dispatch(loadGlobalMaRolesCreator()),
  addUsers: (payload, history) => dispatch(addMaPeopleUsersCreator(payload, history)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddHonorary);
